'use client'

import * as React from 'react'
import { Header } from 'cf-menu/components/header'
import { useLocale, useTranslations } from 'next-intl'
import Image from 'next/image'
import type { Language } from 'codegen/types'
import { trackLoginModalOpen, trackRegistrationModalOpen } from '@repo/analytics/amplitude/utils'
import { defaultLanguage } from 'cf-i18n/constants'
import dynamic from 'next/dynamic'
import { useUserAuthModal } from 'components/user-auth-dialog/user-auth-dialog-context'
import type { HeaderData } from 'cf-menu/contexts/header-config-context'
import { HeaderContextProvider } from 'cf-menu/contexts/header-config-context'
import { MainNavigationContextProvider } from 'cf-menu/contexts/main-navigation-context'
import { usePathname } from '@repo/nextjs-utils/navigation'
import { useLogout } from '@repo/cf-user/hooks/use-user-data'
import { AppRoutes } from '@repo/web-utils/app-routes'
import { useLanguageAndCurrency } from '../../hooks/use-language-and-currency'

export type HeaderChildrenProps = HeaderData & {
  className?: string
}

interface Props {
  classNames?: string
  children?: (props: HeaderChildrenProps) => React.JSX.Element
  forceIsLoggedIn?: boolean
  forceIsAllAccess?: boolean
  isHomepage?: boolean
  navItemsOverride?: any[]
}

export function CfHeader({
  classNames,
  children,
  forceIsLoggedIn,
  forceIsAllAccess,
  isHomepage,
  navItemsOverride,
}: Props) {
  const t = useTranslations('cf_menu')
  const locale = useLocale()
  const pathname = usePathname()
  const { openModal } = useUserAuthModal()
  const onLogoutClick = useLogout({ loginRoute: AppRoutes.LOGIN })
  const { activeCurrency, onChangeCurrency, onChangeLanguage } = useLanguageAndCurrency()

  const onLoginClick = React.useCallback(() => {
    trackLoginModalOpen()
    openModal('login')
  }, [openModal])

  const onSignUpClick = React.useCallback(() => {
    trackRegistrationModalOpen()
    openModal()
  }, [openModal])

  const headerChildrenProps: HeaderChildrenProps = {
    i18n: t,
    activeCurrency,
    className: classNames,
    lang: locale as Language,
    onLoginClick,
    onLogoutClick,
    onSignUpClick,
    forceIsLoggedIn,
    onLanguageChange: onChangeLanguage,
    onCurrencyChange: onChangeCurrency,
    utils: {
      router: {
        pathname,
      },
      Image,
      Link: LinkWithoutPrefetch,
      dynamic,
    },
  }

  return (
    <HeaderContextProvider
      activeCurrency={activeCurrency}
      lang={locale as Language}
      i18n={t}
      onLoginClick={onLoginClick}
      onSignUpClick={onSignUpClick}
      onLogoutClick={onLogoutClick}
      onLanguageChange={onChangeLanguage}
      onCurrencyChange={onChangeCurrency}
      forceIsLoggedIn={forceIsLoggedIn}
      forceIsAllAccess={forceIsAllAccess}
      isHomepage={isHomepage}
      navItemsOverride={navItemsOverride}
      utils={{
        router: {
          pathname,
        },
        Image,
        Link: LinkWithoutPrefetch,
        dynamic,
      }}
    >
      <MainNavigationContextProvider>
        {children ? children(headerChildrenProps) : <Header className={classNames} />}
      </MainNavigationContextProvider>
    </HeaderContextProvider>
  )
}

/*
  Next will prefetch by default the routes that are linked in the app; We don't want this for the headers links because they are not yet all on this app
  @todo Once all routes (linked by the menu) were migrated re-enable prefetching for the header links
*/
export const LinkWithoutPrefetch = ({
  ref,
  ...props
}: React.ComponentProps<'a'> & {
  ref?: React.RefObject<HTMLAnchorElement | null>
}) => {
  const locale = useLocale()
  const { href, ...rest } = props

  const hrefWithLocale = `${locale === defaultLanguage ? '' : `/${locale}`}${href}`

  return <a ref={ref} href={`${href?.startsWith('/') ? hrefWithLocale : href}`} {...rest} />
}

LinkWithoutPrefetch.displayName = 'LinkWithoutPrefetch'
